import { Button, ButtonCus, ButtonCusWithIcon } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem } from '@/components/common/Form';
import TableActionButtons from '@/components/common/TableActionButtons';
import Table from '@/components/table/Table';
import { APPROVE_BIKE_STATUS_CONFIG, BIKE_STATUS_CONFIG, DEFAULT_SELECT_PAGE_SIZES, ROUTES, SUBMIT_MOMENT_DATE_YMD_HM } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { BikeListItem } from '@/types/bike';
import { APPROVE_BIKE_STATUS, COMMON_TYPE, DELETE_STATUS } from '@/types/enums';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { ColumnDef, TableState, Updater } from '@tanstack/react-table';
import { CirclePlus, ListFilter, Search } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import NonRentalDateForm from '../../component/NonRentalDateForm';
import { flowResult } from 'mobx';
import BikeNoteDetail from '@/pages/approve/component/BikeNoteDetail';
import { formatDateTime } from '@/utils/datetime';
import FilterForm from './filter';
import Combobox from '@/components/common/Combobox';
import { cn, isDeleted } from '@/utils/utils';
import DeletedLabel from '@/components/general/DeletedLabel';

export default observer(function BikeManagementOwnerPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    // store
    const { modalStore: { showAlertModal, hideAllModals, showModal },
        bikeStore: { bikeList, totalRecord, paging, keepSearchParams, getAllBike, setObservable, reSendApproveBike, getListModelNameIncluesDelete, bikeModelListIncluesDeleted, defaultPaging },
        accountStore: { getAccounts, paging: accountPaging },
        capacitieStore: { getCapacitieList },
        generalStore: { manufacturers, getManufacturers },
        uiStore: { devicesScreen: { desktop } }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        bikeOwnerId: yup.number(),
        bikeCapacityId: yup.number(),
        bikeModel: yup.string().trim(),
        vehicleStatus: yup.string(),
        locationName: yup.string().trim()
        // findTime: yup.string().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            bikeOwnerId: keepSearchParams?.bikeOwnerId ?? undefined,
            bikeCapacityId: keepSearchParams?.bikeCapacityId ?? undefined,
            bikeModel: keepSearchParams?.bikeModel ?? '',
            // findTime: keepSearchParams?.findTime ?? formatDateTime(new Date(), SUBMIT_MOMENT_DATE_YMD_HM),
            vehicleStatus: keepSearchParams?.vehicleStatus ?? '',
            locationName: keepSearchParams?.locationName ?? ''
        }
    });

    // state
    const [rowSelected, setRowSelected] = useState({});

    // lifecycle
    useEffect(() => {
        if (!keepSearchParams) {
            setObservable('keepSearchParams', { findTime: formatDateTime(new Date(), SUBMIT_MOMENT_DATE_YMD_HM) }, { isMergeObject: false });
        }
        getAllBike(keepSearchParams || { findTime: formatDateTime(new Date(), SUBMIT_MOMENT_DATE_YMD_HM) }, paging);
        getAccounts({ isOwner: true }, { ...accountPaging, pagination: { ...accountPaging.pagination, pageIndex: -2 } });
        getCapacitieList({ type: isPathEk(pathname) ? COMMON_TYPE.EK : COMMON_TYPE.GAS });
        getManufacturers();

        getListModelNameIncluesDelete();
        // return () => {
        //     clean();
        // };
    }, []);

    // function
    const onSearch = (searchParams?: any) => {
        setObservable('keepSearchParams', searchParams, { isMergeObject: false });
        getAllBike(searchParams, defaultPaging);
        hideAllModals();
    };

    const onFetchBike = (searchParams?: any, paging?: TableState) => {
        getAllBike(searchParams, paging);
    };

    const onStateChange = (stateUpdater: Updater<TableState>) => {
        const newPaging = stateUpdater instanceof Function ? stateUpdater(paging) : stateUpdater;
        onFetchBike(keepSearchParams, newPaging);
    };

    const handleClickResendApprove = (id: number) => async () => {
        const res = await flowResult(reSendApproveBike(id));
        if (res) {
            showAlertModal({
                id: 'send-success',
                type: 'success',
                content: t('messages.resend_success'),
                onClickXIcon: () => {
                    onFetchBike(keepSearchParams, paging);
                }
            });
        }
    };

    const handleClickNonRentalDate = () => {
        showModal({
            id: 'non-rental-date',
            size: '3xl',
            title: t('words_title.non_rental_date'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0',
            content: (
                <NonRentalDateForm
                    isPage={false}
                    bikeIds={Object.keys(rowSelected)
                        .filter(key => rowSelected[key])
                        .map(Number)}
                />
            )
        });
    };

    const handleClickEditBike = (id: number) => {
        navigate(`${ROUTES.bikeEdit.href}/${id}`);
    };

    const handleClickNote = (id: number) => {
        showModal({
            id: 'bike-note',
            size: '2xl',
            title: t('words_title.approve_rejeject_resion'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0',
            content: (
                <BikeNoteDetail id={id} />
            )
        });
    };

    const handleOpenFilter = () => {
        showModal({
            id: 'filter',
            size: 'lg',
            title: t('words_title.filter'),
            titleClassName: 'text-center w-full',
            footerClassName: 'p-0 m-0',
            content: <FilterForm form={form} isInModal={true} onSearch={onSearch} />
        });
    };

    // columns
    const columns: ColumnDef<BikeListItem>[] = [
        {
            id: 'actions',
            accessorKey: 'action',
            header: '',
            size: 50,
            enableSorting: false,
            cell: ({ row: { original } }) => (
                <TableActionButtons
                    onView={() => handleClickEditBike(original.id)}
                />
            )
        },
        {
            accessorKey: 'vehicleImageRight',
            header: '',
            size: 200,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[110px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                const imgUrl = original?.vehicleImageRight || original?.vehicleImageLeft || original?.vehicleImageBefore || original?.vehicleImageAfter;
                return (
                    <div className=''>
                        {imgUrl && <img className='max-w-[70px] h-[50px] object-contain' src={imgUrl ?? ''} alt={t('field.bike_image')} />}
                    </div>
                );
            }
        },
        {
            accessorKey: 'bikeManufacture',
            header: t('field.manufacturer'),
            size: 100,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[90px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='break-all min-w-[100px] line-clamp-2'>{isPathEk(pathname) ? original.bikeManufacture || '--' : manufacturers.find(item => item.id === Number(original.bikeManufacture))?.name_jp || '--'}</p>
            )
        },
        {
            accessorKey: 'bikeModel',
            header: t('field.model_name'),
            size: 250,
            cell: ({ row: { original } }) => (
                <div className='break-all min-w-[180px]'>
                    {/* <p className='break-all min-w-[120px] line-clamp-2'>
                        {original.bikeModel || '--'}
                    </p> */}
                    <div className='flex flex-wrap justify-start items-center gap-1.5'>
                        {isDeleted(Number(original?.deleteStatus)) && (
                            <div className='inline-block w-fit h-fit'>
                                <DeletedLabel />
                            </div>
                        )}
                        <span className='line-clamp-2 flex-1 min-w-[120px]'>{original.bikeModel ?? ''}</span>
                    </div>
                </div>
            ),
            enableSorting: false
        },
        {
            accessorKey: 'bikeLicense',
            header: t('field.short_license_plate'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='break-all min-w-[100px] line-clamp-2'>{original.bikeLicense || '--'}</p>
            )
        },
        {
            accessorKey: 'isShowWeb',
            header: t('field.display_on_web'),
            size: 100,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className='min-w-[100px]'>{original.isShowWeb ? t('words_title.yes') : t('words_title.no')}</p>
            )
        },
        {
            accessorKey: 'status',
            header: t('field.status'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                return (
                    // <div className='whitespace-nowrap'>
                    <span className={cn(
                        'whitespace-nowrap',
                        original?.status && `${BIKE_STATUS_CONFIG[original?.status]?.textColor}`
                    )}
                    >
                        {original?.status ? t(`words_title.${BIKE_STATUS_CONFIG[original?.status]?.key}`) : ''}
                    </span>
                    //     {Number(original.deleteStatus) === DELETE_STATUS.IS_DELETE_DISABLE && (
                    //         <DeletedLabel className='ml-1.5' />
                    //     )}
                    // </div>
                );
            }
        },
        {
            accessorKey: 'requestApprove',
            header: t('field.request_approve'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[90px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <div className='min-w-[100px]'>
                    {original.approveStatus === APPROVE_BIKE_STATUS.Reject ?
                        (
                            <ButtonCus
                                variant='outline'
                                className='h-8 px-4 rounded-[4px]'
                                onClick={handleClickResendApprove(original.id)}
                            >
                                {t('button.resend')}
                            </ButtonCus>
                        ) :
                        <></>}
                </div>
            )
        },
        {
            accessorKey: 'approveStatus',
            header: t('field.approve_by_admin'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => (
                <p className={`${APPROVE_BIKE_STATUS_CONFIG[original?.approveStatus]?.textColor} min-w-[100px]`}>{t(`field.${APPROVE_BIKE_STATUS_CONFIG[original?.approveStatus]?.key}`)}</p>
            )
        },
        {
            accessorKey: 'isExistedNote',
            header: t('field.approve_rejeject_resion'),
            size: 150,
            enableSorting: false,
            meta: {
                headerClassName: 'min-w-[100px]',
                cellClassName: ''
            },
            cell: ({ row: { original } }) => {
                return original?.isExistedNote ?
                    (
                        <Button
                            onClick={() => handleClickNote(original.id)}
                            variant='link'
                            size='sm'
                            className='text-link-1 justify-start'
                        >
                            {t('button.view_resion')}
                        </Button>
                    ) :
                    null;
            }

        }
    ];

    const renderButtonTable = () => {
        return (
            <>
                <ButtonCusWithIcon
                    type='button'
                    variant='outline'
                    size='md'
                    className='w-full h-9.5 md:w-52 md:h-12'
                    onClick={handleClickNonRentalDate}
                    disabled={Object.keys(rowSelected).length < 1}
                    iconId='#icon-calender'
                    widthIcon={18}
                    heightIcon={18}
                >
                    {t('button.non_rental_date')}
                </ButtonCusWithIcon>
                <ButtonCus
                    variant='default'
                    size='md'
                    className='w-full h-9.5 md:w-52 md:h-12'
                    onClick={() => {
                        navigate(ROUTES.bikeCreate.href);
                    }}
                >
                    <CirclePlus className='mr-2' />
                    {t('button.add_new')}
                </ButtonCus>
            </>
        );
    };

    return (
        <>
            {desktop ?
                (
                    <div className='layout-card'>
                        <FilterForm form={form} onSearch={onSearch} />
                    </div>
                ) :
                (
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSearch)}>
                            <div className='flex gap-2.5 justify-between relative'>
                                <FormField
                                    control={form.control}
                                    name='bikeModel'
                                    render={({ field }) => (
                                        <FormItem className='flex-1 min-w-0'>
                                            <FormControl>
                                                <div className='relative'>
                                                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 z-10 size-5' />
                                                    <Combobox
                                                        className='h-10 pl-10'
                                                        placeholder={t('field.model_name')}
                                                        value={field.value ?? ''}
                                                        onChange={(item) => {
                                                            field.onChange(item?.value ?? '');
                                                        }}
                                                        search={true}
                                                        items={
                                                            (Array.isArray(bikeModelListIncluesDeleted) && bikeModelListIncluesDeleted?.length > 0 ?
                                                                [
                                                                    ...bikeModelListIncluesDeleted.map(item => ({
                                                                        label: item.bikeModel,
                                                                        value: item.bikeModel,
                                                                        code: item.bikeModel
                                                                    }))
                                                                ] :
                                                                []
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                                <div className=''>
                                    <Button onClick={() => handleOpenFilter()} type='button' variant='outline' size='icon' className='size-10'>
                                        <ListFilter className='size-5' />
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </Form>
                )}
            <div className='grid grid-cols-2 md:hidden gap-4 my-4'>
                {renderButtonTable()}
            </div>
            <div className='layout-card md:mt-6'>
                <Table
                    columns={columns}
                    data={bikeList}
                    state={{
                        ...paging,
                        rowSelection: rowSelected
                    }}
                    rowCount={totalRecord}
                    onStateChange={onStateChange}
                    selectPageSizes={DEFAULT_SELECT_PAGE_SIZES}
                    getRowId={row => row.id?.toString()}
                    // enableRowSelection={true}
                    enableRowSelection={({ original }) => !isDeleted(Number(original.deleteStatus))}
                    onRowSelectionChange={setRowSelected}
                    enableScrollResTable={true}
                    renderActions={(
                        <div className='md:flex hidden justify-end gap-4'>
                            {renderButtonTable()}
                        </div>
                    )}
                />
            </div>
        </>
    );
});
